// Angular imports
import { CurrencyPipe } from '@angular/common';
import { Component, Inject, NgZone, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Params, QueryParamsHandling, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

// Models
import { ApiResponse } from '../models/api-response.model';
import { ItemVariation } from '../models/item-variation.model';
import { Item } from '../models/item.model';
import { SaleItem } from '../models/sale-item.model';
import { SalesSummary } from '../models/sales-summary.model';

// Custom services
import { SeAnalyticsService } from 'se-analytics';
import { SeFePaginationChange, SeFePaginationOptions } from 'se-fe-pagination';
import { CURRENT_ORG_ID } from '../../providers/current-org-id.provider';
import { Sale } from '../models/sale.model';
import { PagedParameters } from '../pagination/pagedParameters';
import { PagingService } from '../pagination/paging.service';
import { ItemVariationService } from '../services/item-variation.service';
import { ItemService } from '../services/item.service';
import { SaleItemService } from '../services/sale-item.service';
import { StoreStatusService } from '../services/store-status.service';

@Component({
  selector: 'app-item-variation-detail',
  standalone: false,
  styleUrls: ['./item-variation-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './item-variation-detail.component.html',
})
export class ItemVariationDetailComponent implements OnInit, OnDestroy {
  variation: ItemVariation;
  variationId: number;
  item: Item;
  itemId = this.activatedRoute.snapshot.params.saleItemId;
  saleItems: SaleItem[];
  saleColumns = ['created_at', 'type', 'sale', 'member', 'price', 'quantity', 'item_total'];
  saleItemLoaded = false;
  salesPaginationOptions: SeFePaginationOptions = {
    label: 'Sales',
    name: 'variationSalesPagination',
    currentPage: 1,
    limit: 25,
    total: 0,
  };
  showOverlay: boolean;
  itemLoaded = false;
  salesSummary: string;
  queryParamSubscription: Subscription;
  externalBaseUrl = environment.snFrontendBaseUrl;
  upfrontAmountSettingText: string;
  showUpfrontAmountText = false;

  constructor(
    @Inject(CURRENT_ORG_ID) public currentOrgId: string,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone,
    private itemService: ItemService,
    private itemVariationService: ItemVariationService,
    private saleItemService: SaleItemService,
    private currencyPipe: CurrencyPipe,
    private pagingService: PagingService,
    private storeStatusService: StoreStatusService,
    private seAnalyticsService: SeAnalyticsService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.variationId = parseInt(this.activatedRoute.snapshot.params.variationId, 10);
    this.setSalesPaginationLabel();
    if (!this.storeStatusService.isSet) {
      this.storeStatusService.setStoreStatus(this.currentOrgId);
    }
    this.loadItem();
    this.subscribeToRouteParams();
    this.seAnalyticsService.trackEvent('pageView', {
      depth1: 'HQ',
      depth2: 'SaleItems',
      depth3: 'Detail',
      depth4: 'Variations',
      depth5: 'Detail',
    });
  }

  ngOnDestroy() {
    this.pagingService.unsubscribe();
    this.queryParamSubscription.unsubscribe();
  }

  public setVariation(item: Item): void {
    this.variation = new ItemVariation(
      item.variations.find((variation: ItemVariation) => variation.id === this.variationId)
    );
  }

  loadItem(): void {
    this.itemLoaded = false;
    this.itemService.getItem(this.itemId).subscribe(
      (response) => this.loadItemSuccess(response),
      (error) => this.loadItemError(error)
    );
  }

  loadItemSuccess(response: ApiResponse<Item>): void {
    this.item = new Item(response.result);
    this.setVariation(this.item);
    this.showUpfrontAmountText = this.variation.price_cents > 0;
    if (this.showUpfrontAmountText) {
      this.upfrontAmountSettingText = this.itemVariationService.upfrontAmountSettingText(this.variation);
    }
    this.itemLoaded = true;
  }

  loadItemError(error: Error): void {
    console.error('loadItemError: ', error);
    throw error;
  }

  subscribeToRouteParams(): void {
    this.queryParamSubscription = this.activatedRoute.queryParamMap.subscribe((params) => {
      this.loadSaleItems();
    });
  }

  combinedApiParams(): Params {
    const params = {
      item_variation_id: this.variationId,
      sold: true,
    };
    return this.pagingService.toApiParams(params);
  }

  loadSaleItems(): void {
    this.showOverlay = true;
    this.saleItemService.getSaleItems(this.combinedApiParams()).subscribe((response) => {
      this.loadSaleItemsSuccess(response);
    });
  }

  loadSaleItemsSuccess(response: ApiResponse<SaleItem[]>): void {
    this.saleItems = response.result.map((saleItem) => new SaleItem(saleItem));
    this.collectSalesPagination(response.metadata.pagination);
    this.generateSalesSummary(response.metadata.summary);
    this.saleItemLoaded = true;
    this.showOverlay = false;
  }

  navigate(params: Params, strategy: QueryParamsHandling = 'merge'): void {
    this.ngZone.run(() => {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: params,
        queryParamsHandling: strategy,
      });
    });
  }

  handleSalesPagination(paginationData: SeFePaginationChange): void {
    const { options, queryParams } = this.pagingService.applyPaginationChange(
      paginationData,
      this.salesPaginationOptions
    );
    this.salesPaginationOptions = options;
    this.navigate(queryParams);
  }

  getSaleUrl(sale: Sale) {
    let salePath = '';
    if (sale.type === 'Invoice') {
      salePath = `/org/${this.currentOrgId}/billing/payments/bills/${sale.id}/detail`;
    } else {
      salePath = `/org/${this.currentOrgId}/orders/${sale.id}/detail`;
    }
    return environment.snFrontendBaseUrl + salePath;
  }

  getMemberUrl(personaId) {
    return `${environment.snFrontendBaseUrl}/org/${this.currentOrgId}/people/${personaId}/profile`;
  }

  generateSalesSummary(summaryObj: SalesSummary): void {
    const currency = this.getCurrency(this.saleItems);
    const totalAmountInDollars = summaryObj.total_amount / 100;
    const formattedTotalAmount = this.currencyPipe.transform(totalAmountInDollars, currency, 'symbol', '1.2-2');
    this.salesSummary = `${summaryObj.total_quantity} sold • ${formattedTotalAmount} total`;
  }

  private getCurrency(saleItems: SaleItem[]): string {
    return saleItems.length ? saleItems[0].currency : 'USD';
  }

  private collectSalesPagination(pagination: PagedParameters | undefined): void {
    this.salesPaginationOptions = this.pagingService.syncPaginationFromResponse(
      pagination,
      this.salesPaginationOptions
    );
  }

  private setSalesPaginationLabel(): void {
    this.salesPaginationOptions = {
      ...this.salesPaginationOptions,
      label: this.translateService.instant('VARIATION_DETAIL_VIEW.sales') || 'Sales',
    };
  }
}
